import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { getValidToken } from "../../../utils/authUtils";
import { useReportFilter } from "../context/ReportFilterContext";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
} from "../../../utils/utils";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import { PROJECT_TERMINOLOGY } from "../../../project/project_module/enums";
import { useState, useEffect } from "react";
import { Loading } from "../../../components/Loading";
import { OperationType } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import {
  CartesianGrid,
  Tooltip,
  XAxis,
  Legend,
  Bar,
  ResponsiveContainer,
  BarChart,
  YAxis,
  LabelList,
} from "recharts";

interface FuelRecord {
  scope: "PROJECT" | "COMPANY";
  subScope: string;
  companyName: string;
  projectName: string;
  clientName: string;
  vehicleNumber: string;
  expenseDate: string;
  fuelCard: string;
  petrolPumpName: string;
  location: string;
  liters: string;
  pricePerLitre: string;
  bulkDiscountApplied: string;
  amount: string;
  superProjectName: string;
  superProjectId: string;
  projectId: string;
  expenseId: string;
}

interface SummaryRecord {
  summaryType: "COMBINED_SUMMARY" | "COMPANY_SUMMARY" | "PROJECT_SUMMARY";
  subscope: "ALL" | "PROJECT" | "RENTAL" | "TRIP" | "VEHICLE";
  totalLiters: string;
  avgPricePerLitre: string;
  avgBulkDiscount: string;
  totalAmount: string;
  totalRecords: string;
}

interface vehicleWiseSummary {
  vehicleId: string;
  vehicleNumber: string;
  total_litres: string;
  avg_price_per_litre: string;
  avg_bulk_discount_applied: string;
  total_amount: string;
}

interface FuelReportData {
  dateRange: {
    start: string;
    end: string;
    rangeType: string;
  };
  records: FuelRecord[];
  summaryRecords: SummaryRecord[];
  vehicleWiseSummary: vehicleWiseSummary[];
  pagination: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

const FuelOverallReport = () => {
  const { showToast } = useToast();
  const { filterState } = useReportFilter();
  const {
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicles,
    selectedVehicleIds,
    selectedProviderIds,
    selectedProjectIds,
  } = filterState;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Vehicle display state
  const [showAllVehicles, setShowAllVehicles] = useState(false);

  const { planStatus } = usePlanStatus();
  const showProjectName = [
    OperationType.ALL,
    OperationType.LARGE_FLEET_ALL,
    OperationType.CONTRACT,
    OperationType.SCHOOL,
  ].includes(planStatus?.operationType);

  const {
    data: reportData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "fuelOverallReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
      selectedVehicleIds,
      selectedProviderIds,
      selectedProjectIds,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/fuel-stored-procedure-summary/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/fuel-stored-procedure-summary/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicles.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }

      if (selectedVehicleIds.length > 0) {
        params.vehicleIds = selectedVehicleIds;
      }

      if (selectedProviderIds.length > 0) {
        params.providerIds = selectedProviderIds;
      }

      if (selectedProjectIds.length > 0) {
        params.projectIds = selectedProjectIds;
      }

      // Add pagination parameters
      params.page = currentPage;
      params.limit = pageSize;

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.isSuccess) {
        return response.data.data as FuelReportData;
      } else {
        showToast(response.data.message, "error");
        return null;
      }
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicles,
    selectedVehicleIds,
    selectedProviderIds,
    selectedProjectIds,
  ]);

  const summaryRecords = reportData?.summaryRecords || [];
  const records = reportData?.records || [];
  const vehicleWiseSummary = reportData?.vehicleWiseSummary || [];
  const pagination = reportData?.pagination;

  // Helper to get summary by subscope
  const getSummaryBySubscope = (subscope: string) =>
    summaryRecords.find((summary) => summary.subscope === subscope);

  // For each tab, get the correct summary
  const allSummary = getSummaryBySubscope("ALL");

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = event.target.value as number;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Total Fuel Expense</Typography>
          <Typography variant="h4" fontSize={15}>
            {formatCurrency(allSummary?.totalAmount || "0")}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Total Liters</Typography>
          <Typography variant="h4" fontSize={15}>
            {parseFloat(allSummary?.totalLiters || "0").toFixed(2)} L
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Avg Price/L</Typography>
          <Typography variant="h4" fontSize={15}>
            {formatCurrency(allSummary?.avgPricePerLitre || "0")}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Total Discount</Typography>
          <Typography variant="h4" fontSize={15}>
            {formatCurrency(allSummary?.avgBulkDiscount || "0")}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderFuelTable = (records: FuelRecord[]) => (
    <Paper sx={{ p: 2 }}>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyles}>Category</TableCell>
              {(planStatus?.operationType === OperationType.LARGE_FLEET ||
                planStatus?.operationType ===
                  OperationType.LARGE_FLEET_ALL) && (
                <>
                  <TableCell sx={tableHeaderStyles}>
                    {PROJECT_TERMINOLOGY.SINGULAR}
                  </TableCell>
                  <TableCell sx={tableHeaderStyles}>Division</TableCell>
                </>
              )}
              <TableCell sx={tableHeaderStyles}>Date</TableCell>
              <TableCell sx={tableHeaderStyles}>Vehicle</TableCell>
              <TableCell sx={tableHeaderStyles}>Petrol Pump</TableCell>
              <TableCell sx={tableHeaderStyles} align="right">
                Liters
              </TableCell>
              <TableCell sx={tableHeaderStyles} align="right">
                Price/L
              </TableCell>
              <TableCell sx={tableHeaderStyles} align="right">
                Discount
              </TableCell>
              <TableCell sx={tableHeaderStyles} align="right">
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => (
              <TableRow
                key={index}
                sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
              >
                <TableCell sx={tableCellStyles}>
                  {record?.subScope || "-"}
                </TableCell>
                {(planStatus?.operationType === OperationType.LARGE_FLEET ||
                  planStatus?.operationType ===
                    OperationType.LARGE_FLEET_ALL) && (
                  <>
                    <TableCell sx={tableCellStyles}>
                      {record?.superProjectName}
                    </TableCell>
                    <TableCell sx={tableCellStyles}>
                      {record?.projectName || "-"}
                    </TableCell>
                  </>
                )}
                <TableCell sx={tableCellStyles}>
                  {formatDate(record.expenseDate)}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {formatVehicleNumber(record.vehicleNumber)}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {record.petrolPumpName !== "n/a"
                    ? `${record.petrolPumpName} ${
                        record.location && `(${record.location})`
                      }`
                    : "-"}
                </TableCell>
                <TableCell sx={tableCellStyles} align="right">
                  {parseFloat(record.liters).toFixed(2)}
                </TableCell>
                <TableCell sx={tableCellStyles} align="right">
                  {formatCurrency(record.pricePerLitre)}
                </TableCell>
                <TableCell sx={tableCellStyles} align="right">
                  {parseFloat(record.bulkDiscountApplied) > 0
                    ? formatCurrency(record.bulkDiscountApplied)
                    : "-"}
                </TableCell>
                <TableCell sx={tableCellStyles} align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary.main"
                  >
                    {formatCurrency(record.amount)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: 80, fontSize: "0.75rem" }}
            >
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                displayEmpty
                size="small"
                sx={{ fontSize: "0.75rem" }}
              >
                <MenuItem value={20} sx={{ fontSize: "0.75rem" }}>
                  20
                </MenuItem>
                <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>
                  50
                </MenuItem>
                <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>
                  100
                </MenuItem>
                <MenuItem value={200} sx={{ fontSize: "0.75rem" }}>
                  200
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {`${
                (pagination.currentPage - 1) * pagination.pageSize + 1
              }-${Math.min(
                pagination.currentPage * pagination.pageSize,
                pagination.totalCount
              )} of ${pagination.totalCount}`}
            </Typography>
          </Box>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Paper>
  );

  const renderFuelChart = () => {
    if (vehicleWiseSummary?.length <= 1) return null;
    return (
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom fontSize={16}>
            Fuel Expense by Vehicle
          </Typography>
          <Box
            sx={{
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
              "&::-webkit-scrollbar-corner": {
                backgroundColor: "#f1f1f1",
              },
              overflowX: "auto",
              overflowY: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                minWidth: `${Math.max(vehicleWiseSummary.length * 50, 600)}px`, // Dynamically calculate width
                height: "400px",
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={vehicleWiseSummary}
                  margin={{ top: 40, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="vehicleNumber"
                    fontSize={10}
                    interval={0}
                    angle={vehicleWiseSummary.length > 20 ? -45 : 0}
                    textAnchor={
                      vehicleWiseSummary.length > 20 ? "end" : "middle"
                    }
                    height={vehicleWiseSummary.length > 20 ? 60 : 30}
                  />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    stroke="#8884d8"
                    fontSize={10}
                    tickFormatter={(value) => `${value} L`}
                    tickCount={8}
                    domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.9)]}
                    allowDataOverflow
                  />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === "Total Amount"
                        ? formatCurrency(value)
                        : name === "Total Liters"
                        ? `${value} L`
                        : value,
                      name,
                    ]}
                    labelStyle={{ fontSize: "12px" }}
                    contentStyle={{ fontSize: "11px" }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="total_litres"
                    name="Total Liters"
                    fill="#8884d8"
                    barSize={vehicleWiseSummary.length < 12 ? 40 : 20}
                  >
                    <LabelList
                      dataKey="total_litres"
                      position="top"
                      content={(props: any) => {
                        const { x, y, width, value, index } = props;
                        const data = vehicleWiseSummary[index];
                        if (!data) return null;

                        const totalAmount = parseFloat(
                          data?.total_amount || "0"
                        );
                        const totalLiters = parseFloat(
                          data?.total_litres || "0"
                        );
                        const labelY = Math.max(y - 20, 30);
                        const fontSize = vehicleWiseSummary.length > 12 ? 6 : 8;

                        return (
                          <text
                            x={x + width / 2}
                            y={labelY}
                            textAnchor="middle"
                            fontSize={fontSize}
                            fill="#333"
                          >
                            <tspan x={x + width / 2} dy="-8">
                              {formatCurrency(totalAmount)}
                            </tspan>
                            <tspan x={x + width / 2} dy="10">
                              {totalLiters.toFixed(2)} L
                            </tspan>
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  //  vehicle wise summary
  const renderVehicleTable = () => {
    if (!vehicleWiseSummary?.length || vehicleWiseSummary.length <= 1) return null;

    const INITIAL_DISPLAY_COUNT = 8;
    const vehiclesToShow = showAllVehicles
      ? vehicleWiseSummary
      : vehicleWiseSummary.slice(0, INITIAL_DISPLAY_COUNT);

    const hasMoreVehicles = vehicleWiseSummary.length > INITIAL_DISPLAY_COUNT;

    // Calculate total for percentages
    const totalFuelAmount = vehicleWiseSummary.reduce(
      (sum, item) => sum + parseFloat(item.total_amount),
      0
    );

    return (
      <Box sx={{ mb: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#323130",
              mb: 0.5,
            }}
          >
            Vehicle Fuel Consumption
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#605e5c",
              fontSize: "0.8rem",
            }}
          >
            Top fuel consumption and costs by vehicle
          </Typography>
        </Box>

        {/* Power BI Style Cards Grid */}
        <Grid container spacing={1.5}>
          {vehiclesToShow.map((item, index) => {
            const amount = parseFloat(item.total_amount);
            const liters = parseFloat(item.total_litres);
            const avgPrice = parseFloat(item.avg_price_per_litre);
            const percentage = (amount / totalFuelAmount) * 100;

            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    bgcolor: "white",
                    border: "1px solid #e1dfdd",
                    borderRadius: 1,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      elevation: 3,
                      transform: "translateY(-1px)",
                      borderColor: "#0078d4",
                    },
                  }}
                >
                  {/* Ranking Badge */}
                  {index < 3 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        bgcolor:
                          index === 0
                            ? "#ff6b35"
                            : index === 1
                            ? "#f7931e"
                            : "#ffcc02",
                        color: "white",
                        borderRadius: "50%",
                        width: 18,
                        height: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}

                  {/* Content */}
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Vehicle Number */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: "#323130",
                        letterSpacing: 0.3,
                        mb: 0.8,
                        lineHeight: 1.2,
                        fontFamily: "monospace",
                      }}
                    >
                      {formatVehicleNumber(item.vehicleNumber)}
                    </Typography>

                    {/* Amount - Big Number */}
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "#323130",
                        lineHeight: 1,
                        mb: 0.5,
                      }}
                    >
                      {formatCurrency(amount)}
                    </Typography>

                    {/* Liters - Fuel specific metric */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#ff6b35",
                        mb: 0.8,
                      }}
                    >
                      {liters.toFixed(1)} L
                    </Typography>

                    {/* Footer Stats */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: "auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: "#605e5c",
                          fontWeight: 500,
                        }}
                      >
                        ₹{avgPrice.toFixed(1)}/L
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: "#0078d4",
                          fontWeight: 600,
                        }}
                      >
                        {percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Show More Button */}
        {hasMoreVehicles && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="text"
              onClick={() => setShowAllVehicles(!showAllVehicles)}
              sx={{
                fontSize: "0.8rem",
                textTransform: "none",
                color: "#0078d4",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "rgba(0, 120, 212, 0.04)",
                },
              }}
            >
              {showAllVehicles
                ? `Show Less`
                : `View All ${vehicleWiseSummary.length} Vehicles`}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderNoRecords = () => {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}
          align="center"
        >
          No records found
        </Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Loading overlay for refetching */}
      {isLoading && <Loading fullScreen={false} message="Loading..." />}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing..." />
      )}
      {renderSummaryCards()}

      {renderFuelChart()}
      {renderVehicleTable()}
      {records?.length > 0 ? renderFuelTable(records) : renderNoRecords()}
    </Box>
  );
};

export default FuelOverallReport;
